import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShiftMasterEntry } from './hooks/useShiftMasterEntry';
import AppDropdown from '../../../components/AppDropdown';
import {
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  AMPM_OPTIONS,
  SHIFT_TYPE_OPTIONS,
} from './api/shiftMasterApi';

export default function ShiftMasterEntryScreen() {
  const { state, actions } = useShiftMasterEntry();
  const navigate = useNavigate();
  const { form, isEdit, isLoading, isSaving } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Shift Details...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Shift' : 'Create Shift'}
          </h1>
        </div>

        <Link
          to="/payroll/shift-master"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout matching Screenshot 2 & Attendance Rules */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card (lg:col-span-8) */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Shift Master</h2>
          </div>

          {/* Form Body matching Screenshot 2 */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-4">
              {/* Shift Name */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Shift Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => actions.patchForm('name', e.target.value)}
                  placeholder="e.g. General Shift"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Alias */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Alias
                </label>
                <input
                  type="text"
                  value={form.alias}
                  onChange={(e) => actions.patchForm('alias', e.target.value)}
                  placeholder="e.g. GN"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* In time (Hour : Minute : AM/PM) */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  In time <span className="text-error">*</span>
                </label>
                <div className="flex items-center gap-2 max-w-xs">
                  <div className="flex-1">
                    <AppDropdown
                      options={HOUR_OPTIONS}
                      value={form.inHour}
                      onChange={(val) => actions.patchForm('inHour', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center"
                    />
                  </div>
                  <span className="text-xs font-bold text-on-surface-variant">:</span>
                  <div className="flex-1">
                    <AppDropdown
                      options={MINUTE_OPTIONS}
                      value={form.inMinute}
                      onChange={(val) => actions.patchForm('inMinute', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center"
                    />
                  </div>
                  <div className="w-20">
                    <AppDropdown
                      options={AMPM_OPTIONS}
                      value={form.inAmPm}
                      onChange={(val) => actions.patchForm('inAmPm', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Out Time (Hour : Minute : AM/PM) */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Out Time <span className="text-error">*</span>
                </label>
                <div className="flex items-center gap-2 max-w-xs">
                  <div className="flex-1">
                    <AppDropdown
                      options={HOUR_OPTIONS}
                      value={form.outHour}
                      onChange={(val) => actions.patchForm('outHour', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center"
                    />
                  </div>
                  <span className="text-xs font-bold text-on-surface-variant">:</span>
                  <div className="flex-1">
                    <AppDropdown
                      options={MINUTE_OPTIONS}
                      value={form.outMinute}
                      onChange={(val) => actions.patchForm('outMinute', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center"
                    />
                  </div>
                  <div className="w-20">
                    <AppDropdown
                      options={AMPM_OPTIONS}
                      value={form.outAmPm}
                      onChange={(val) => actions.patchForm('outAmPm', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Shift Type */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Shift Type <span className="text-error">*</span>
                </label>
                <div className="max-w-xs">
                  <AppDropdown
                    options={SHIFT_TYPE_OPTIONS}
                    value={form.shiftType}
                    onChange={(val) => actions.patchForm('shiftType', val)}
                    triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Row matching Screenshot 2 */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => actions.handleSave({ exitAfter: true })}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">save</span>
              <span>{isSaving ? 'Saving...' : 'save & eXit'}</span>
            </button>

            {!isEdit && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => actions.handleSave({ exitAfter: false })}
                className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Save &amp; continue
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/payroll/shift-master')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Written Context Panel (lg:col-span-4) matching Screenshot 2 */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">
              lightbulb
            </span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Define daily shifts, core working hours, and shift categories for automated attendance verification and grace calculations.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              GUIDELINES:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Shift In/Out:</strong> Specify official work start and end times in 12-hour AM/PM format.
              </li>
              <li>
                <strong className="text-on-surface">Shift Type:</strong> Day, Night, or Rotational classifications determine overnight punch reconciliation.
              </li>
              <li>
                <strong className="text-on-surface">Alias:</strong> Use a short code (e.g. GN, MS, NS) for compact display in rosters and schedules.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
